import {Duration} from '../Domain/Duration';
import {Stroke} from '../Models/Stroke';
import {Distance} from '../Domain/Distance';
import {Power} from '../Domain/Power';
import {StrokeRate} from '../Domain/StrokeRate';
import {Speed} from '../Domain/Speed';
import {Energy} from '../Domain/Energy';
import {Force} from '../Domain/Force';
import {Fraction} from '../Domain/Fraction';
import {HeartRate} from '../Domain/HeartRate';
import {Curve} from '../Models/Curve';
import {RangeInput} from '../Models/RangeInput';
import {averageCurve} from './averageCurve';

export interface RangeOverview {
    totalDuration: Duration;
    totalDistance: Distance;
    averageDistancePerStroke: Distance | null;
    averagePower: Power | null;
    averageStrokeRate: StrokeRate | null;
    averageSpeed: Speed | null;
    averageStrokeLength: Distance | null;
    averageEnergy: Energy | null;
    averagePeakForce: Force | null;
    averagePeakForcePosition: Distance | null;
    relativePeakForcePosition: Fraction | null;
    averageDriveTime: Duration | null;
    averageRecoverTime: Duration | null;
    averageEnergyPerStroke: Energy | null;
    heartRate: HeartRate | null;
    curve: Curve;
    strokes: Stroke[];
}

function getRange(start: Duration, end: Duration, strokes: Stroke[]): Stroke[] {
    const firstIndex = strokes.findIndex((stroke: Stroke) => {
        return stroke.totalTime.gte(start);
    });
    const lastIndex = strokes.findLastIndex((stroke: Stroke) => {
        return stroke.totalTime.lte(end);
    });

    return strokes.slice(firstIndex, lastIndex + 1);
}

export function calculateRangeOverview(input: RangeInput, strokes: Stroke[]): RangeOverview {
    const selectedStrokes = getRange(input.start, input.end, strokes);
    const totalDuration = input.predetermined.totalDuration
        ? input.predetermined.totalDuration
        : input.end.subtract(input.start);
    const totalDistance = input.predetermined.totalDistance
        ? input.predetermined.totalDistance
        : selectedStrokes.reduce(
              (sum, stroke) => (stroke.distancePerStroke === null ? sum : sum.add(stroke.distancePerStroke)),
              Distance.zero(),
          );
    
    return {
        totalDuration: totalDuration,
        totalDistance: totalDistance,
        averageSpeed: Speed.fromDistanceAndDuration(totalDistance, totalDuration),
        averageDistancePerStroke: Distance.average(
            ...selectedStrokes
                .filter((stroke) => stroke.distancePerStroke === null)
                .map((stroke) => stroke.distancePerStroke!),
        ),
        averagePower: Power.average(
            ...selectedStrokes.filter((stroke) => stroke.power !== null).map((stroke) => stroke.power!),
        ),
        averageStrokeRate: StrokeRate.average(
            ...selectedStrokes.filter((stroke) => stroke.strokeRate !== null).map((stroke) => stroke.strokeRate!),
        ),
        averageStrokeLength: Distance.average(
            ...selectedStrokes.filter((stroke) => stroke.strokeLength !== null).map((stroke) => stroke.strokeLength!),
        ),
        averageEnergy: Energy.average(
            ...selectedStrokes.filter((stroke) => stroke.energy !== null).map((stroke) => stroke.energy!),
        ),
        averagePeakForce: Force.average(
            ...selectedStrokes.filter((stroke) => stroke.peakForce !== null).map((stroke) => stroke.peakForce!),
        ),
        averagePeakForcePosition: Distance.average(
            ...selectedStrokes
                .filter((stroke) => stroke.peakForcePosition !== null)
                .map((stroke) => stroke.peakForcePosition!),
        ),
        relativePeakForcePosition: Fraction.average(
            ...selectedStrokes
                .filter((stroke) => stroke.relativePeakForcePosition !== null)
                .map((stroke) => stroke.relativePeakForcePosition!),
        ),
        averageDriveTime: Duration.average(
            ...selectedStrokes.filter((stroke) => stroke.driveTime !== null).map((stroke) => stroke.driveTime!),
        ),
        averageRecoverTime: Duration.average(
            ...selectedStrokes.filter((stroke) => stroke.recoverTime !== null).map((stroke) => stroke.recoverTime!),
        ),
        averageEnergyPerStroke: Energy.average(
            ...selectedStrokes.filter((stroke) => stroke.energy !== null).map((stroke) => stroke.energy!),
        ),
        heartRate: HeartRate.average(
            ...selectedStrokes
                .map((stroke) => stroke.heartRate)
                .filter((heartRate): heartRate is HeartRate => heartRate !== null),
        ),
        curve: averageCurve(
            selectedStrokes.map((stroke) => stroke.curve).filter((curve): curve is Curve => curve !== null),
        ),
        strokes: selectedStrokes,
        ...input.predetermined,
    };
}
